import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { CircularProgress } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { ImageBox } from '../../../components/BoxedImage';
import { ComplianceLayerLegend } from '../../../components/ComplianceLayerLegend';
import { ImageViewer } from '../../../components/ImageViewer';
import { BBox, BoxAction, CameraPog, PogBBox, Rect } from '../../../types';
import { COMPLIANCE_COLOR } from '../../store-details/components/PlanogramComplianceControlCenter';

interface CameraImageViewerProps {
  // Camera image props
  preloadedImage?: string;
  fallbackPogBoxColor?: string;
  selectedPogBoxColor?: string;
  highlightedIlItemId?: number;
  filteredBboxes: BBox[];
  boxActions: BoxAction[];
  productRecognitionDetections: ImageBox[];
  planogramComplianceActions: ImageBox[];
  showProductImages: boolean;
  onBoxClick: (bbox: BBox) => void;
  onProductRecognitionClick?: (detection: ImageBox, detectionIndex: number) => void;
  onProductRecognitionRightClick?: (detection: ImageBox | PogBBox) => void;
  onPlanogramComplianceClick?: (detection: ImageBox) => void;
  titleActions?: React.ReactNode;
  onBoxesSelected?: (selectedBboxes: BBox[]) => void;

  // Edit mode props
  editMode?: boolean;
  isEditAvailable?: boolean;
  isEditLoading?: boolean;
  isTaskAssignedToCurrentUser?: boolean;
  onLockAndEdit?: () => void;
  selectedShape?: ImageBox | null;
  selectedDetectionIndex?: number | null;
  onEditModeEnable?: () => void;
  onRemoveShape?: () => void;
  onSaveChanges?: () => void;
  onCancelChanges?: () => void;
  onCornerDragStart?: (event: React.MouseEvent | React.TouchEvent, cornerIndex: number) => void;
  onDetectionEdited?: (updatedDetection: ImageBox) => void;
  onMouseUpWhileDragging?: () => void;
  onBoxClear?: () => void;
  onNewShapeCreated?: (rect: [number, number, number, number]) => void;

  // POG images props
  showPogs: boolean;
  currentPogs: CameraPog[];
  preloadedImagesB64: Record<string, string>;
  selectedIlItemIds: Set<number>;
  planogramStatusMap: Map<number, string>;
  onPogBoxClick: (bbox: PogBBox) => void;
  onPogBoxRightClick?: (bbox: ImageBox | PogBBox) => void;
  selectedComplianceLayers?: Set<string>;
  onToggleComplianceLayer?: (layer: string) => void;

  // Optional override for the middle (compliance) column. When provided,
  // replaces the compliance ImageViewer entirely.
  complianceColumnContent?: React.ReactNode;
  // Optional content rendered in the title row above the middle column.
  complianceColumnTitle?: React.ReactNode;
}

export const CameraImageViewer: React.FC<CameraImageViewerProps> = (props) => {
  const {
    // Camera image props
    preloadedImage,
    fallbackPogBoxColor,
    filteredBboxes,
    highlightedIlItemId,
    boxActions,
    productRecognitionDetections,
    planogramComplianceActions,
    showProductImages,
    onBoxClick,
    onProductRecognitionClick,
    onProductRecognitionRightClick,
    titleActions,
    onBoxesSelected,

    // Edit mode props
    editMode,
    isEditAvailable = true,
    isEditLoading = false,
    isTaskAssignedToCurrentUser = true,
    selectedShape,
    onEditModeEnable,
    onLockAndEdit,
    onRemoveShape,
    onSaveChanges,
    onCancelChanges,
    onDetectionEdited,
    onMouseUpWhileDragging,
    onBoxClear,
    onNewShapeCreated,

    // POG images props
    showPogs,
    currentPogs,
    preloadedImagesB64,
    selectedIlItemIds,
    planogramStatusMap,
    onPlanogramComplianceClick,
    onPogBoxClick,
    onPogBoxRightClick,
    selectedComplianceLayers,
    onToggleComplianceLayer,
    complianceColumnContent,
    complianceColumnTitle,
  } = props;
  const selectedPogBoxColor = props.selectedPogBoxColor || '#2563eb';

  const isEditablePolygon = !!(editMode && selectedShape?.isPolygon && selectedShape?.points);

  const handleRemovePoint = useCallback(() => {
    if (!selectedShape?.points || selectedShape.points.length <= 4) return;

    // Find the point whose removal causes the least shape change
    // by finding the shortest distance between consecutive points
    const points = selectedShape.points;
    let minImpact = Infinity;
    let removeIndex = 0;

    for (let i = 0; i < points.length; i++) {
      const prev = points[(i - 1 + points.length) % points.length];
      const next = points[(i + 1) % points.length];
      // Impact = how much the polygon changes = distance from point to the line between prev and next
      const dx = next.x - prev.x;
      const dy = next.y - prev.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      const impact =
        len === 0 ? 0 : Math.abs(dx * (prev.y - points[i].y) - dy * (prev.x - points[i].x)) / len;

      if (impact < minImpact) {
        minImpact = impact;
        removeIndex = i;
      }
    }

    const newPoints = points.filter((_, i) => i !== removeIndex);
    const xs = newPoints.map((p) => p.x);
    const ys = newPoints.map((p) => p.y);

    const updatedBox: ImageBox = {
      ...selectedShape,
      points: newPoints,
      shape: [
        Math.min(...xs),
        Math.min(...ys),
        Math.max(...xs) - Math.min(...xs),
        Math.max(...ys) - Math.min(...ys),
      ] as Rect,
    };

    if (onDetectionEdited) onDetectionEdited(updatedBox);
  }, [selectedShape, onDetectionEdited]);

  const handleAddPoint = useCallback(() => {
    if (!selectedShape?.points) return;

    // Find the longest edge and add a midpoint
    const points = selectedShape.points;
    let maxDist = 0;
    let insertAfter = 0;

    for (let i = 0; i < points.length; i++) {
      const next = points[(i + 1) % points.length];
      const dist = Math.sqrt((next.x - points[i].x) ** 2 + (next.y - points[i].y) ** 2);
      if (dist > maxDist) {
        maxDist = dist;
        insertAfter = i;
      }
    }

    const p1 = points[insertAfter];
    const p2 = points[(insertAfter + 1) % points.length];
    const midpoint = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };

    const newPoints = [
      ...points.slice(0, insertAfter + 1),
      midpoint,
      ...points.slice(insertAfter + 1),
    ];

    const xs = newPoints.map((p) => p.x);
    const ys = newPoints.map((p) => p.y);

    const updatedBox: ImageBox = {
      ...selectedShape,
      points: newPoints,
      shape: [
        Math.min(...xs),
        Math.min(...ys),
        Math.max(...xs) - Math.min(...xs),
        Math.max(...ys) - Math.min(...ys),
      ] as Rect,
    };

    if (onDetectionEdited) onDetectionEdited(updatedBox);
  }, [selectedShape, onDetectionEdited]);

  // Decode image to get dimensions for DragOverlay
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    if (preloadedImage?.startsWith('data:')) {
      const img = new Image();
      img.onload = () => {
        setImageDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };
      img.src = preloadedImage;
    }
  }, [preloadedImage]);

  return (
    <div
      className={`grid grid-cols-2 ${
        showPogs && currentPogs.length > 0 ? 'lg:grid-cols-3' : ''
      } gap-4`}
    >
      {/* Camera Image */}
      <div className="space-y-2">
        <div className="flex items-center justify-between min-h-10">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">Camera Images</h3>
            {editMode && selectedShape && (
              <>
                <button
                  onClick={onRemoveShape}
                  className="px-3 py-1 rounded text-sm font-medium bg-red-600 text-white hover:bg-red-700"
                >
                  Remove Shape
                </button>
                {isEditablePolygon && (
                  <>
                    <button
                      onClick={handleAddPoint}
                      className="px-2 py-1 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                      title="Add point on longest edge"
                    >
                      <AddIcon fontSize="small" />
                    </button>
                    <button
                      onClick={handleRemovePoint}
                      disabled={!selectedShape?.points || selectedShape.points.length <= 4}
                      className="px-2 py-1 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Remove least impactful point"
                    >
                      <RemoveIcon fontSize="small" />
                    </button>
                  </>
                )}
              </>
            )}
            {!editMode ? (
              isTaskAssignedToCurrentUser ? (
                <button
                  onClick={onEditModeEnable}
                  className="px-3 py-1 rounded text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 flex items-center gap-1"
                >
                  Edit OD Layers
                </button>
              ) : (
                <button
                  onClick={onLockAndEdit}
                  disabled={!isEditAvailable || isEditLoading}
                  title={!isEditAvailable ? 'Task is locked by another user' : ''}
                  className="px-3 py-1 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  {isEditLoading && <CircularProgress size={14} color="inherit" />}
                  {isEditLoading ? 'Locking...' : 'Lock task & start editing'}
                </button>
              )
            ) : (
              <>
                <button
                  onClick={onCancelChanges}
                  className="px-3 py-1 rounded text-sm font-medium bg-gray-500 text-white hover:bg-gray-600"
                >
                  Discard changes
                </button>
                <button
                  onClick={onSaveChanges}
                  className="px-3 py-1 rounded text-sm font-medium bg-green-600 text-white hover:bg-green-700"
                >
                  Save Changes
                </button>
              </>
            )}
          </div>
          {titleActions}
        </div>
        <div className="relative">
          <ImageViewer<BBox>
            imageBase64={preloadedImage?.startsWith('data:') ? preloadedImage : undefined}
            bboxes={filteredBboxes}
            onBoxClick={onBoxClick}
            onBoxRightClick={onProductRecognitionRightClick}
            boxActions={boxActions}
            productRecognitionDetections={productRecognitionDetections}
            onProductRecognitionClick={onProductRecognitionClick}
            enableZoom={true}
            showProductImages={showProductImages}
            onBoxesSelected={onBoxesSelected}
            editMode={editMode}
            selectedDetection={selectedShape}
            onDetectionEdited={onDetectionEdited}
            onMouseUpWhileDragging={onMouseUpWhileDragging}
            onBoxClear={onBoxClear}
            onNewShapeCreated={onNewShapeCreated}
            imageSrc={preloadedImage}
            imageDimensions={imageDimensions || undefined}
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="min-h-10 flex items-center">{complianceColumnTitle}</div>
        {complianceColumnContent ? (
          complianceColumnContent
        ) : (
          <ImageViewer<BBox>
            imageBase64={preloadedImage?.startsWith('data:') ? preloadedImage : undefined}
            bboxes={[]}
            onBoxClick={() => {}}
            boxActions={[]}
            productRecognitionDetections={[]}
            planogramComplianceActions={planogramComplianceActions}
            enableZoom={true}
            showProductImages={showProductImages}
            onPlanogramComplianceClick={onPlanogramComplianceClick}
          />
        )}
      </div>

      {/* POG Images - only show if there are POGs and showPogs is enabled */}
      {showPogs && currentPogs.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between min-h-10">
            <h3 className="text-lg font-semibold">POG Images ({currentPogs.length})</h3>
            {/* Compliance Legend */}
            {selectedComplianceLayers && onToggleComplianceLayer && (
              <ComplianceLayerLegend
                selectedComplianceLayers={selectedComplianceLayers}
                planogramStatusMap={planogramStatusMap}
                onToggleComplianceLayer={onToggleComplianceLayer}
              />
            )}
          </div>
          <div className={`grid grid-cols-1 lg:grid-cols-${currentPogs.length || 1} gap-4`}>
            {currentPogs.map((pog) => {
              const preloadedPogImage = pog?.pogImageUri
                ? preloadedImagesB64[pog.pogImageUri]
                : undefined;

              // Map each pog bbox to include color based on compliance status
              const pogBboxesWithColor = (pog?.bboxes ?? []).map((bbox) => {
                let color = fallbackPogBoxColor ?? '#3b82f6'; // fallback color

                // Check if this POG item should be highlighted
                // Since both planogram and realogram clicks now use comprehensive selection,
                // we just need to check if this item is in the selected set
                let isSelected = false;
                if (selectedIlItemIds.size > 0) {
                  isSelected = selectedIlItemIds.has(bbox.ilItemId);
                }

                // If highlighted, use blue color. Otherwise, use compliance status color
                if (highlightedIlItemId && bbox.ilItemId === highlightedIlItemId) {
                  color = '#FFFF00';
                } else if (isSelected) {
                  color = selectedPogBoxColor;
                } else if (bbox.ilItemId && planogramStatusMap.has(bbox.ilItemId)) {
                  const status = planogramStatusMap.get(bbox.ilItemId);
                  if (status && status in COMPLIANCE_COLOR) {
                    color = COMPLIANCE_COLOR[status as keyof typeof COMPLIANCE_COLOR];
                  }
                }
                return { ...bbox, color };
              });

              return (
                <div key={`pog-${pog?.ilSectionId}`}>
                  {pog?.pogImageUri ? (
                    <ImageViewer<PogBBox>
                      imageBase64={
                        preloadedPogImage?.startsWith('data:') ? preloadedPogImage : undefined
                      }
                      bboxes={pogBboxesWithColor}
                      onBoxClick={onPogBoxClick}
                      onBoxRightClick={onPogBoxRightClick}
                      boxActions={[]}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-48 bg-gray-100 rounded">
                      <p className="text-gray-500 text-sm">No POG image available</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
